import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, User, MapPin } from 'lucide-react';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { ApprovalRequest } from '../../../services/approvalService';
import { format } from 'date-fns';

function getLocationKey(request: ApprovalRequest): string {
  const loc = request.requestData?.location;
  if (!loc) return '__no_location__';
  if (typeof loc === 'string') return loc;
  const o = loc as { _id?: string; name?: string };
  if (o._id) return o._id;
  if (o.name) return String(o.name);
  return '__no_location__';
}

function getLocationDisplayName(request: ApprovalRequest): string {
  const loc = request.requestData?.location;
  if (!loc) return 'No location';
  if (typeof loc === 'string') return loc;
  const o = loc as { name?: string };
  return o.name || 'Unknown location';
}

interface ApprovalGroupedTableProps {
  approvalRequests: ApprovalRequest[];
  loading?: boolean;
  onApprove?: (request: ApprovalRequest) => void;
  onReject?: (request: ApprovalRequest) => void;
  onView?: (request: ApprovalRequest) => void;
  onDelete?: (request: ApprovalRequest) => void;
  isApprover?: boolean;
  isRequester?: boolean;
  groupByLocation?: boolean;
}

const ApprovalGroupedTable: React.FC<ApprovalGroupedTableProps> = ({
  approvalRequests,
  loading = false,
  onApprove,
  onReject,
  onView,
  onDelete,
  isApprover = false,
  isRequester = false,
  groupByLocation = false,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const groupedRequests = useMemo(() => {
    const grouped: Record<string, ApprovalRequest[]> = {};
    approvalRequests.forEach((request) => {
      const key = groupByLocation ? getLocationKey(request) : request.requesterId._id;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(request);
    });
    return grouped;
  }, [approvalRequests, groupByLocation]);

  const toggleGroup = (userId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleRow = (requestId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(requestId)) next.delete(requestId);
      else next.add(requestId);
      return next;
    });
  };

  const statusSeverity = (status: string) =>
    status === 'pending' ? 'warning' : status === 'approved' ? 'success' : 'danger';
  const actionLabel: Record<string, string> = { create: 'Create', update: 'Update', delete: 'Delete' };
  const actionSeverity: Record<string, 'success' | 'info' | 'danger' | 'secondary'> = {
    create: 'success',
    update: 'info',
    delete: 'danger',
  };

  const formatResourceType = (resourceType: string) =>
    resourceType
      .split(/(?=[A-Z])/)
      .join(' ')
      .replace(/^\w/, (c) => c.toUpperCase());

  const keyFieldsByType: Record<string, string[]> = {
    asset: ['name', 'code', 'moduleType', 'serialNumber'],
    manufacturer: ['name', 'contactPerson', 'email'],
    customer: ['name', 'contactPerson', 'email'],
    location: ['name', 'address', 'city'],
    user: ['name', 'email', 'phone'],
    checkpoint: ['name', 'description', 'category'],
    projectType: ['name', 'description'],
  };

  const getKeyData = (data: Record<string, unknown>, resourceType: string): string[] => {
    const fields = keyFieldsByType[resourceType] || ['name', 'title', 'description'];
    const values: string[] = [];
    fields.forEach((field) => {
      const v = data[field];
      if (v !== undefined && v !== null && v !== '') {
        const label = field.replace(/([A-Z])/g, ' $1').replace(/^\w/, (c) => c.toUpperCase()).trim();
        values.push(`${label}: ${String(v)}`);
      }
    });
    return values.length > 0 ? values : ['—'];
  };

  const hasMoreData = (data: Record<string, unknown>, resourceType: string): boolean => {
    const fields = keyFieldsByType[resourceType] || ['name', 'title', 'description'];
    const filled = Object.keys(data).filter(
      (k) => data[k] !== undefined && data[k] !== null && data[k] !== '' && typeof data[k] !== 'object'
    );
    return filled.length > fields.length;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="flex flex-col items-center gap-3">
          <div className="w-9 h-9 border-2 border-gray-200 border-t-gray-700 rounded-full animate-spin" />
          <p className="text-xs font-medium text-gray-500">Loading…</p>
        </div>
      </div>
    );
  }

  if (approvalRequests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50">
        <User className="w-10 h-10 text-gray-300 mb-2" />
        <p className="text-sm font-medium text-gray-700">No approval requests</p>
        <p className="text-xs text-gray-500 mt-0.5">There are no requests to show</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm font-[family-name:var(--font-outfit)]">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="w-8 px-3 py-2.5" />
              <th className="px-3 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Resource</th>
              <th className="px-3 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Key data</th>
              <th className="px-3 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Status</th>
              <th className="px-3 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Date</th>
              <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {Object.entries(groupedRequests).map(([groupKey, requests]) => {
              const isGroupExpanded = expandedGroups.has(groupKey);
              const requesterName = requests[0]?.requesterName ?? 'Unknown';
              const requesterEmail = requests[0]?.requesterEmail ?? '';
              const locationName = groupByLocation ? getLocationDisplayName(requests[0]!) : null;
              return (
                <React.Fragment key={groupKey}>
                  <tr className="bg-gray-50/80 hover:bg-gray-50 transition-colors">
                    <td colSpan={6} className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => toggleGroup(groupKey)}
                        className="w-full flex items-center justify-between gap-2 py-2 text-left"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {isGroupExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          )}
                          {groupByLocation ? (
                            <>
                              <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-3.5 h-3.5 text-blue-600" />
                              </div>
                              <div className="min-w-0">
                                <span className="text-sm font-medium text-gray-900 truncate block">{locationName}</span>
                                <span className="text-[11px] text-gray-500 block">Location</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="w-7 h-7 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                                <User className="w-3.5 h-3.5 text-gray-600" />
                              </div>
                              <div className="min-w-0">
                                <span className="text-sm font-medium text-gray-900 truncate block">{requesterName}</span>
                                {requesterEmail && (
                                  <span className="text-[11px] text-gray-500 truncate block">{requesterEmail}</span>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        <Tag
                          value={`${requests.length} ${requests.length === 1 ? 'request' : 'requests'}`}
                          severity="secondary"
                          className="!text-[10px] !px-2 !py-0.5"
                        />
                      </button>
                    </td>
                  </tr>
                  {isGroupExpanded &&
                    requests.map((request) => {
                      const isRowExpanded = expandedRows.has(request._id);
                      const keyData = getKeyData(request.requestData, request.resourceType);
                      const hasMore = hasMoreData(request.requestData, request.resourceType);
                      return (
                        <React.Fragment key={request._id}>
                          <tr className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-3 py-2 w-8">
                              {hasMore && (
                                <button
                                  type="button"
                                  onClick={() => toggleRow(request._id)}
                                  className="p-0.5 text-gray-400 hover:text-gray-600"
                                >
                                  {isRowExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </button>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex flex-col gap-0.5">
                                <Tag
                                  value={actionLabel[request.action] ?? request.action}
                                  severity={actionSeverity[request.action] ?? 'secondary'}
                                  className="!text-[10px] !px-2 !py-0.5 w-fit"
                                />
                                <span className="text-xs font-medium text-gray-900">{formatResourceType(request.resourceType)}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 max-w-[220px]">
                              <div className="text-[11px] text-gray-700 space-y-0.5">
                                {keyData.slice(0, 2).map((line, idx) => (
                                  <div key={idx} className="truncate" title={line}>
                                    {line}
                                  </div>
                                ))}
                                {keyData.length > 2 && !isRowExpanded && (
                                  <button
                                    type="button"
                                    onClick={() => toggleRow(request._id)}
                                    className="text-[11px] font-medium text-gray-600 hover:text-gray-900"
                                  >
                                    +{keyData.length - 2} more
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <Tag value={request.status} severity={statusSeverity(request.status)} className="!text-[10px] !px-2 !py-0.5" />
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-600">
                              {format(new Date(request.createdAt), 'MMM d, yyyy')}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-end gap-1">
                                {onView && (
                                  <Button
                                    icon="pi pi-eye"
                                    className="p-button-text p-button-sm"
                                    tooltip="View"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={() => onView(request)}
                                  />
                                )}
                                {isApprover && request.status === 'pending' && (
                                  <>
                                    {onApprove && (
                                      <Button
                                        icon="pi pi-check"
                                        className="p-button-text p-button-sm p-button-success"
                                        tooltip="Approve"
                                        tooltipOptions={{ position: 'top' }}
                                        onClick={() => onApprove(request)}
                                      />
                                    )}
                                    {onReject && (
                                      <Button
                                        icon="pi pi-times"
                                        className="p-button-text p-button-sm p-button-danger"
                                        tooltip="Reject"
                                        tooltipOptions={{ position: 'top' }}
                                        onClick={() => onReject(request)}
                                      />
                                    )}
                                  </>
                                )}
                                {isRequester && request.status === 'pending' && onDelete && (
                                  <Button
                                    icon="pi pi-trash"
                                    className="p-button-text p-button-sm p-button-danger"
                                    tooltip="Delete"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={() => onDelete(request)}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                          {isRowExpanded && (
                            <tr className="bg-gray-50/50">
                              <td />
                              <td colSpan={5} className="px-3 py-2">
                                <div className="rounded-lg border border-gray-200 bg-white p-3">
                                  <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-2">Request data</p>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
                                    {Object.entries(request.requestData).map(([key, value]) => {
                                      if (value == null || value === '' || typeof value === 'object') return null;
                                      const label = key.replace(/([A-Z])/g, ' $1').replace(/^\w/, (c) => c.toUpperCase()).trim();
                                      return (
                                        <div key={key} className="flex gap-2">
                                          <span className="text-gray-500 shrink-0">{label}:</span>
                                          <span className="text-gray-900 break-words">{String(value)}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {request.approverComments && (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                      <p className="text-[10px] font-semibold text-gray-600 mb-1">
                                        {request.status === 'approved' ? 'Approval comments' : 'Rejection reason'}
                                      </p>
                                      <p className="text-[11px] text-gray-800">{request.approverComments}</p>
                                      {request.approverName && (
                                        <p className="text-[10px] text-gray-500 mt-0.5">— {request.approverName}</p>
                                      )}
                                    </div>
                                  )}
                                  {isApprover && request.status === 'pending' && (
                                    <div className="mt-3 pt-3 border-t border-gray-200 flex gap-2 justify-end">
                                      {onApprove && (
                                        <Button label="Approve" icon="pi pi-check" className="p-button-sm p-button-success" onClick={() => onApprove(request)} />
                                      )}
                                      {onReject && (
                                        <Button label="Reject" icon="pi pi-times" className="p-button-sm p-button-danger" onClick={() => onReject(request)} />
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ApprovalGroupedTable;
